import { DataTable, DataTableSortStatus } from 'mantine-datatable';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { ActionIcon, Button, Group, TextInput, Tooltip } from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import {
  IconFlag,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
  IconX,
} from '@tabler/icons-react';

import {
  EthnicitiesSortBy,
  Ethnicity,
  GetEthnicitiesRequestParams,
} from '@interfaces/ethnicities.interface';

import {
  useCountEthnicitiesQuery,
  useDeleteEthnicityMutation,
  useGetManyEthnicitiesQuery,
} from '@api/ethnicities.api';
import { isApiError } from '@api/index';

import useLayoutProps from '@hooks/useLayoutProps';
import useStateWithDebounce from '@hooks/useStateWithDebouce';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import UpsertEthnicityForm, {
  UPSERT_ETHNICITY_FORM_MODAL_ID,
} from '@components/ethnicities/UpsertEthnicityForm';

export default function EthnicitiesList() {
  // ==========================================================================
  // General
  // ==========================================================================
  const canCreateEthnicities = useUserPermissionGuard('ETHNICITIES.CREATE');
  const canEditEthnicities = useUserPermissionGuard('ETHNICITIES.UPDATE');
  const canDeleteEthnicities = useUserPermissionGuard('ETHNICITIES.DELETE');

  // ==========================================================================
  // State
  // ==========================================================================
  const [filters, setFilters] = useState<GetEthnicitiesRequestParams>({
    page: +1,
    pageLength: +10,
    sortBy: 'id',
    sortOrder: 'asc',
  });

  const [nameSearch, setNameSearch, nameSearchDebounced] =
    useStateWithDebounce('');

  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Ethnicity>>({
    columnAccessor: 'name',
    direction: 'asc',
  });

  useEffect(() => {
    // Skip the initial mount
    if (
      filters.sortBy === sortStatus.columnAccessor &&
      filters.sortOrder === sortStatus.direction
    ) {
      return;
    }

    setFilters((filters) => ({
      ...filters,
      sortBy: sortStatus.columnAccessor as EthnicitiesSortBy,
      sortOrder: sortStatus.direction,
    }));
  }, [sortStatus]);

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: ethnicitiesCount = { count: 0 }, isLoading: isLoadingCount } =
    useCountEthnicitiesQuery({
      nameSearch: nameSearchDebounced,
    });

  const { data = [], isLoading } = useGetManyEthnicitiesQuery({
    ...filters,
    nameSearch: nameSearchDebounced,
  });

  const [deleteEthnicity, { isLoading: isLoadingDeleteEthnicity }] =
    useDeleteEthnicityMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleAddEthnicityClick = useCallback(() => {
    openModal({
      modalId: UPSERT_ETHNICITY_FORM_MODAL_ID,
      title: 'Aggiungi etnia',
      children: <UpsertEthnicityForm />,
    });
  }, []);

  const handleEditEthnicityClick = useCallback((ethnicity: Ethnicity) => {
    openModal({
      modalId: UPSERT_ETHNICITY_FORM_MODAL_ID,
      title: 'Modifica etnia',
      children: <UpsertEthnicityForm ethnicity={ethnicity} />,
    });
  }, []);

  const handleDeleteEthnicityClick = useCallback((id: string) => {
    openConfirmModal({
      title: 'Elimina etnia',
      children:
        "Stai per eliminare l'etnia. L'operazione non è reversibile.\nContinuare?",

      confirmProps: { color: 'red' },
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      onConfirm: async () => {
        try {
          await deleteEthnicity(id).unwrap();

          showNotification({
            title: 'Etnia eliminata',
            message: 'Etnia eliminata con successo',
          });
        } catch (e) {
          if (isApiError(e)) {
            if (e.data.code === 'P2003') {
              showNotification({
                title: 'Impossibile eliminare',
                message: "L'etnia è in uso da un paziente",
                color: 'red',
                icon: <IconX />,
              });
            } else {
              showNotification({
                title: 'Errore',
                message: e.data.message,
                color: 'red',
                icon: <IconX />,
              });
            }
          } else {
            showNotification({
              title: 'Errore',
              message: 'Errore sconosciuto',
              color: 'red',
              icon: <IconX />,
            });
            console.error(e);
          }
        }
      },
    });
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => (
      <Group>
        <IconFlag />
        Etnie {!isLoadingCount ? `(${ethnicitiesCount.count})` : ''}
      </Group>
    ),
    [ethnicitiesCount.count, isLoadingCount],
  );

  const rightSection = useMemo(
    () =>
      canCreateEthnicities ? (
        <Button leftSection={<IconPlus />} onClick={handleAddEthnicityClick}>
          Aggiungi etnia
        </Button>
      ) : (
        <></>
      ),
    [handleAddEthnicityClick, canCreateEthnicities],
  );

  useLayoutProps({
    title,
    rightSection,
  });

  return (
    <>
      <DataTable
        /* General */
        striped
        noRecordsText="Nessuna etnia trovata"
        minHeight={data.length === 0 ? 200 : undefined}
        /* Data */
        fetching={isLoading || isLoadingCount}
        columns={[
          {
            accessor: 'name',
            title: 'Nome',
            sortable: true,
            filter: (
              <TextInput
                placeholder="Ricerca nome"
                leftSection={<IconSearch size={16} />}
                rightSection={
                  nameSearch.trim() !== '' ? (
                    <ActionIcon
                      size="sm"
                      variant="transparent"
                      c="dimmed"
                      onClick={() => setNameSearch('')}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  ) : undefined
                }
                value={nameSearch}
                onChange={(e) => setNameSearch(e.currentTarget.value)}
              />
            ),
          },
          {
            accessor: 'actions',
            title: '',
            textAlign: 'right',
            width: '0%',
            render: (record) => (
              <Group wrap="nowrap" gap="xs">
                {canEditEthnicities && (
                  <Tooltip label="Modifica etnia">
                    <ActionIcon
                      variant="subtle"
                      onClick={() => handleEditEthnicityClick(record)}
                    >
                      <IconPencil />
                    </ActionIcon>
                  </Tooltip>
                )}
                {canDeleteEthnicities && (
                  <Tooltip label="Elimina etnia">
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      disabled={isLoadingDeleteEthnicity}
                      onClick={() => handleDeleteEthnicityClick(record.id)}
                    >
                      <IconTrash />
                    </ActionIcon>
                  </Tooltip>
                )}
              </Group>
            ),
          },
        ]}
        records={data}
        /* Sorting */
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        /* Pagination */
        page={filters.page!}
        recordsPerPage={filters.pageLength!}
        totalRecords={ethnicitiesCount.count}
        paginationText={() => ''}
        recordsPerPageLabel=""
        recordsPerPageOptions={[10, 20, 50]}
        onPageChange={(newPage) => setFilters({ ...filters, page: newPage })}
        onRecordsPerPageChange={(newPageLength) =>
          setFilters({ ...filters, pageLength: newPageLength, page: 1 })
        }
      />
    </>
  );
}
